import Axios from 'axios';
import queryString from 'query-string';
import { decode } from 'next-auth/jwt';
import { getSession } from 'next-auth/react';
import { isServer } from '@tanstack/react-query';

import { combine, deepCamelizeKeys, deepSnakeizeKeys } from '~/utils';

export interface IFetchApiProps {
  signal?: AbortSignal;
}

export const api = Axios.create({
  baseURL: `${(process.env.NEXT_PUBLIC_URL_API || '').replace(/\/+$/, '')}/v1`,
  headers: {
    Accept: '*/*',
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  async function (config) {
    if (isServer) {
      const salt = combine(
        {
          joinWith: '',
        },
        process.env.NODE_ENV === 'production' ? '__Secure-' : '',
        'authjs.session-token',
      );

      const { cookies } = await import('next/headers');
      const cookieStore = await cookies();

      let accessToken;

      const encryptedToken = cookieStore
        .getAll()
        .reduce<string>((result, c) => {
          if (!c.name.includes(salt) || !c.value) {
            return result;
          }

          result += c.value;

          return result;
        }, '');

      try {
        const jwt = await decode({
          salt,
          token: encryptedToken,
          secret: process.env.AUTH_SECRET || '',
        });

        accessToken = jwt?.accessToken as string;
      } catch {
        accessToken = '';
      }

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    } else {
      const session = await getSession();

      if (session?.accessToken) {
        config.headers.Authorization = `Bearer ${session.accessToken}`;
      }
    }

    config.paramsSerializer = (params) => {
      return queryString.stringify(params, {
        encode: false,
        arrayFormat: 'bracket',
      });
    };

    if (config.headers['Content-Type'] === 'multipart/form-data') {
      return config;
    }

    if (config.params) {
      config.params = deepSnakeizeKeys(config.params);
    }

    if (config.data) {
      config.data = deepSnakeizeKeys(config.data);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => {
    if (response.data) {
      const camelizedData = deepCamelizeKeys(response.data);

      if (camelizedData.hasOwnProperty('meta')) {
        return [camelizedData.data, camelizedData.meta];
      }

      return camelizedData.data;
    }

    return response;
  },
  async function (error) {
    return Promise.reject(error.response.data);
  },
);
